import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit2, Trash2, Upload, MousePointerClick, Eye } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", image_url: "", link_url: "", placement: "home_top", is_active: true };
const PLACEMENTS = [
  { value: "home_top", label: "मुखपृष्ठ ऊपर (Home Top)" },
  { value: "home_middle", label: "मुखपृष्ठ बीच में (Home Middle)" },
  { value: "article_top", label: "समाचार ऊपर (Article Top)" },
  { value: "article_bottom", label: "समाचार नीचे (Article Bottom)" },
];

export default function AdsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/ads/admin").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...EMPTY, ...a }); setEditId(a.id); setOpen(true); };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      // Ad banners should NOT be watermarked (advertiser owns their creative)
      const { data } = await api.post("/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
    } catch(err) { toast.error(formatError(err)); }
  };

  const submit = async () => {
    if (!form.title || !form.image_url) return toast.error("शीर्षक और बैनर image ज़रूरी है");
    try {
      if (editId) { await api.put(`/ads/${editId}`, form); toast.success("अपडेट हो गया"); }
      else { await api.post("/ads", form); toast.success("विज्ञापन जोड़ा गया"); }
      setOpen(false); load();
    } catch(err) { toast.error(formatError(err)); }
  };
  const del = async (id) => {
    if (!window.confirm("क्या हटाना है?")) return;
    try { await api.delete(`/ads/${id}`); load(); }
    catch(err) { toast.error(formatError(err)); }
  };
  const toggle = async (a) => {
    try { await api.put(`/ads/${a.id}`, { is_active: !a.is_active }); load(); }
    catch(err) { toast.error(formatError(err)); }
  };

  return (
    <div data-testid="ads-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">विज्ञापन प्रबंधन</h1>
          <p className="text-sm text-slate-500 mt-1">कुल {items.length} बैनर — साइट पर 4 स्लॉट्स उपलब्ध</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F172A] hover:bg-[#1E3A8A]" onClick={openNew} data-testid="add-ad-btn"><Plus className="w-4 h-4 mr-1.5"/>नया बैनर</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editId ? "बैनर संपादित करें" : "नया विज्ञापन बैनर"}</DialogTitle><DialogDescription>बैनर image अपलोड करें और स्थान (placement) चुनें</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div><Label>शीर्षक</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="विज्ञापनदाता का नाम" data-testid="ad-title"/></div>
              <div>
                <Label>बैनर image</Label>
                <div className="flex items-center gap-3">
                  <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="URL या अपलोड" data-testid="ad-image"/>
                  <label className="cursor-pointer flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-2 text-sm hover:bg-slate-50">
                    <Upload className="w-4 h-4"/>
                    <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="ad-upload"/>
                  </label>
                </div>
                {form.image_url && <img src={form.image_url} alt="" className="mt-2 h-24 rounded object-cover border"/>}
              </div>
              <div><Label>लिंक (क्लिक करने पर जायें)</Label><Input value={form.link_url} onChange={(e) => setForm({...form, link_url: e.target.value})} placeholder="https://..." data-testid="ad-link"/></div>
              <div>
                <Label>स्थान (Placement)</Label>
                <Select value={form.placement} onValueChange={(v) => setForm({...form, placement: v})}>
                  <SelectTrigger data-testid="ad-placement"><SelectValue/></SelectTrigger>
                  <SelectContent>{PLACEMENTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 pt-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({...form, is_active: v})} data-testid="ad-active"/> सक्रिय
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>रद्द</Button>
              <Button onClick={submit} className="bg-[#0F172A] hover:bg-[#1E3A8A]" data-testid="save-ad">सहेजें</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>बैनर</TableHead><TableHead>शीर्षक</TableHead><TableHead>स्थान</TableHead>
            <TableHead>दिखा</TableHead><TableHead>क्लिक</TableHead><TableHead>CTR</TableHead><TableHead>स्थिति</TableHead><TableHead className="text-right">कार्य</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((a) => {
              const imp = a.impressions || 0; const clk = a.clicks || 0;
              const ctr = imp > 0 ? ((clk / imp) * 100).toFixed(1) + "%" : "—";
              return (
              <TableRow key={a.id} data-testid={`ad-row-${a.id}`}>
                <TableCell><img src={a.image_url} alt="" className="h-10 w-24 object-cover rounded border"/></TableCell>
                <TableCell className="font-medium">{a.title}</TableCell>
                <TableCell><Badge variant="secondary" className="font-mono text-[10px]">{a.placement}</Badge></TableCell>
                <TableCell><span className="inline-flex items-center gap-1 text-xs"><Eye className="w-3 h-3"/>{imp}</span></TableCell>
                <TableCell><span className="inline-flex items-center gap-1 text-xs"><MousePointerClick className="w-3 h-3"/>{clk}</span></TableCell>
                <TableCell className="text-xs font-semibold text-[#1E3A8A]">{ctr}</TableCell>
                <TableCell><button onClick={() => toggle(a)}><Badge variant={a.is_active ? "default" : "secondary"}>{a.is_active ? "सक्रिय" : "निष्क्रिय"}</Badge></button></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`edit-ad-${a.id}`}><Edit2 className="w-4 h-4"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(a.id)} data-testid={`del-ad-${a.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </TableCell>
              </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">कोई विज्ञापन नहीं — पहला बैनर जोड़ें</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
